import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAppContext } from '../context/AppContext';
import type { SavedProposalDoc } from './ProposalsModal';
import { WelcomeDashboard } from './welcome/WelcomeDashboard';
import { hasResumableDraft } from './welcome/metrics';

/**
 * Post-login home screen. Loads the signed-in designer's own proposals and
 * hands them to WelcomeDashboard, which owns all of the presentation.
 */
export default function WelcomePage() {
  const {
    currentUser, userName, setMode, handleNewProposal, setIsProposalsModalOpen, loadProposalFromCloud,
    client, specItems, pricingRows, proposalType, resolvedModelName,
  } = useAppContext();

  const [proposals, setProposals] = useState<SavedProposalDoc[]>([]);
  const [loading, setLoading] = useState(true);
  // One frozen "now" per visit — every relative day count on the page agrees.
  const [now] = useState(() => new Date());

  useEffect(() => {
    if (!db || !currentUser) return;
    // Deliberately the signed-in user's own proposals only, admins included:
    // this screen is a personal summary, not the org-wide analytics dashboard.
    const q = query(collection(db, 'proposals'), where('designerId', '==', currentUser.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list: SavedProposalDoc[] = [];
      snapshot.forEach(docSnap => {
        list.push({ ...(docSnap.data() as SavedProposalDoc), id: docSnap.id });
      });
      setProposals(list);
      setLoading(false);
    }, (err) => {
      console.error('Failed to load proposals for welcome summary:', err);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser]);

  // Only retail proposals persist to the localStorage draft (AppContext restores
  // client/config/specItems/pricingRows on mount, independently of `mode`), so a
  // resumable draft only ever exists while proposalType is 'retail'.
  const draftSummary = useMemo(() => {
    if (proposalType !== 'retail') return null;
    const draft = hasResumableDraft({
      clientName: client.clientName,
      model: resolvedModelName,
      specItemCount: specItems.length,
      hasPricing: pricingRows.some(r => Number(r.listPrice) > 0),
    });
    if (!draft) return null;
    return { clientLabel: client.clientName.trim(), modelLabel: resolvedModelName };
  }, [proposalType, client.clientName, resolvedModelName, specItems.length, pricingRows]);

  return (
    <WelcomeDashboard
      proposals={proposals}
      loading={loading}
      firstName={(userName || currentUser?.displayName || currentUser?.email || '').split(' ')[0]}
      now={now}
      onOpenProposal={(p) => { loadProposalFromCloud(p); setMode('edit'); }}
      onNewProposal={(type) => { if (handleNewProposal(type)) setMode('edit'); }}
      onOpenArchive={() => setIsProposalsModalOpen(true)}
      draftSummary={draftSummary}
      onResumeDraft={() => setMode('edit')}
    />
  );
}
